import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sanitizeText } from "@/lib/sanitize";
import { getCurrentUser } from "@/lib/authUser";

interface NameAIDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  isGroupWorkspace: boolean;
  groupId?: string | null;
  onNameUpdated: () => void;
}

export function NameAIDialog({
  open,
  onOpenChange,
  currentName,
  isGroupWorkspace,
  groupId,
  onNameUpdated,
}: NameAIDialogProps) {
  const [aiName, setAiName] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  

  const handleSave = async () => {
    if (!aiName.trim()) {
      toast.error("Error", { description: "AI Assistant name cannot be empty" });
      return;
    }

    if (aiName.trim().length > 50) {
      toast.error("Error", { description: "AI Assistant name must be less than 50 characters" });
      return;
    }

    setIsLoading(true);
    const sanitizedName = sanitizeText(aiName, 50);

    try {
      if (isGroupWorkspace && groupId) {
        // Update group AI name
        const { error } = await supabase
          .from("groups")
          .update({ ai_assistant_name: sanitizedName })
          .eq("id", groupId);

        if (error) throw error;
      } else {
        // Update personal AI name
        const user = await getCurrentUser();
        if (!user) throw new Error("Not authenticated");

        const { error } = await supabase
          .from("profiles")
          .update({ ai_assistant_name: sanitizedName })
          .eq("user_id", user.id);

        if (error) throw error;
      }

      toast.success("Success", { description: `AI Assistant renamed to "${sanitizedName}"` });
      
      onNameUpdated();
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Name Your AI Assistant</DialogTitle>
          <DialogDescription>
            Give your AI assistant a custom name that will appear in the chat box.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="ai-name">AI Assistant Name</Label>
            <Input
              id="ai-name"
              value={aiName}
              onChange={(e) => setAiName(e.target.value)}
              placeholder="Enter a name (e.g., Jarvis, Helper, Assistant)"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              {aiName.length}/50 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Name"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
